import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { Select } from '../../../../components/forms/Select';
import { UseFormReturn, useWatch } from 'react-hook-form';
import {
  FacebookSearchContentType,
  FacebookSearchSortType,
  FacebookSearchType,
  ImportSourceArgsGroupInput,
  PlanImporterType,
  Network,
  SocialOAuthProvider,
  SocialProfileFields_FacebookSocialProfile_Fragment,
  ThirdPartyProfile
} from '../../../../models/graphql';
import {
  SelectDropdownTemplate,
  SelectItem,
  SelectItemTemplate,
  SelectViewProps
} from '../../../../components/forms/Select/Select.view';
import { ListItem } from '../../../../components/actions/ListItem';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { SocialNetworksEnum } from '../../../../constants/social-networks';
import classNames from 'classnames';
import classes from '../ContentFilter/ContentFilter.module.scss';
import { ContentFilterSubFilterWrapper } from '../ContentFilter';
import { SelectAutoComplete } from '../../../../components/forms/SelectAutoComplete';
import { SourceLevelAction } from '../SourceLevelAction';
import { FindContentContext, SearchStepEnum } from '../FindContentProvider';
import {
  autoCompleteProfileDropDownTemplate,
  autoCompleteProfileDropDownTemplateProps
} from '../SelectTemplate/SelectTemplate.view';
import { FormElementValueType } from '../../../../models/basic-objects';
import { useDidMount } from '../../../../hooks/useDidMount';
import { SearchErrorBox } from '../SearchErrorBox';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { generateProfileList } from '../findContentHelpers';
import { Input } from '../../../../components/forms/Input';
import { useDimensions } from '../../../../hooks/useDimensions';
import { BreakpointsValuesEnum } from '../../../../constants/breakpoints-values';
import { AdvancedFeature } from 'components/containers/AdvancedFeature';
import { AdvancedFeatures } from 'components/containers/AdvancedFeature/AdvancedFeature.view';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';

export type ContentFilterFacebookViewProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  selectedAutoCompleteValue: FormElementValueType;
  setSelectedAutoCompleteValue: (value: FormElementValueType) => void;
  facebookProfiles: SocialProfileFields_FacebookSocialProfile_Fragment[];
  submitFormHandler: (search: FacebookSearchType | FacebookSearchTypesEnum, profileId: string) => void;
  filterSearchHandler: (resetFilterBar?: boolean) => void;
  hasPosts: boolean;
  showSaveSourceModal: boolean;
  setShowSaveSourceModal: (show: boolean) => void;
  getFacebookInputArgs: () => ImportSourceArgsGroupInput;
  thirdPartyProfiles: ThirdPartyProfile[];
  profileSearchHistory: ThirdPartyProfile[];
  autoCompleteLoading: boolean;
  firstConnectedPageProfileId?: string;
  apolloErrorKey: string | undefined;
  resetCallBackHandler: CallableFunction;
};

export type FacebookFilterBarProps = {
  formInstance: UseFormReturn;
  filterSearchHandler: (resetFilterBar?: boolean) => void;
  width: number;
};

export enum FacebookSearchTypesEnum {
  FACEBOOK_PAGES = 'FACEBOOK_PAGES',
  MY_POSTS = 'MY_POSTS'
}

export const FacebookFilterBar = (props: FacebookFilterBarProps): JSX.Element => {
  const { formInstance, width } = props;
  const { loading } = useContext(FindContentContext);
  const { importType, isTrial } = useOrganizationPlan();
  const isDisabledFeature = importType !== PlanImporterType.ADVANCED_IMPORTER;
  const filterBarContainerClasses = classNames('grid-x');

  // In place of setting default values, we determine if no value is set and do so
  // Setting default values will break loading a saved source
  useDidMount(() => {
    const { contentType, sortType } = formInstance.getValues();
    if (!contentType) {
      formInstance.setValue('contentType', FacebookSearchContentType.ALL);
    }
    if (!sortType) {
      formInstance.setValue('sortType', FacebookSearchSortType.LATEST);
    }
  });

  const filterDefaultWrapperClasses = classNames('cell', classes.InputWrapper, {
    'small-6': width < BreakpointsValuesEnum.large
  });
  const filterInputWrapperClasses = classNames('cell', classes.InputWrapper, classes.HasButtonSibling, {
    'auto margin-top-small-8': width < BreakpointsValuesEnum.large,
    auto: width >= BreakpointsValuesEnum.large,
    [classes.InputWrapperSmallFilterWidth]: width >= BreakpointsValuesEnum.large
  });
  const filterButtonWrapperClasses = classNames('cell', classes.FilterButtonContainer, {
    'shrink margin-top-small-8': width < BreakpointsValuesEnum.large
  });

  return (
    <>
      <div className={filterBarContainerClasses}>
        <div className={filterDefaultWrapperClasses}>
          <Select
            items={[
              { value: FacebookSearchContentType.ALL, label: 'All Content' },
              { value: FacebookSearchContentType.LINKS, label: 'All Links' },
              { value: FacebookSearchContentType.PHOTOS, label: 'All Photos' },
              { value: FacebookSearchContentType.VIDEOS, label: 'All Videos' }
            ]}
            name={'contentType'}
            size={'medium'}
            rules={{}}
            useFormInstance={formInstance}
            disabled={loading || isDisabledFeature}
          />
        </div>
        <div className={filterDefaultWrapperClasses}>
          <Select
            items={[
              { value: FacebookSearchSortType.LATEST, label: 'Latest' },
              { value: FacebookSearchSortType.MOST_COMMENTED, label: 'Most Commented' },
              { value: FacebookSearchSortType.MOST_LIKED, label: 'Most Liked' },
              { value: FacebookSearchSortType.MOST_SHARED, label: 'Most Shared' }
            ]}
            name={'sortType'}
            size={'medium'}
            rules={{}}
            useFormInstance={formInstance}
            disabled={loading || isDisabledFeature}
          />
        </div>
        <div className={filterInputWrapperClasses}>
          <Input
            formInstance={formInstance}
            name={'term'}
            placeholder={'Search within these posts'}
            rules={{}}
            size={'medium'}
            disabled={loading || isDisabledFeature}
            onKeyDown={e => {
              // ENTER key presses submits form
              if (e.key === 'Enter') {
                props.filterSearchHandler();
              }
            }}
          />
        </div>
        <div className={filterButtonWrapperClasses}>
          <Button
            type={'button'}
            variant={'hollow'}
            size={'medium'}
            color={ButtonColorsEnum.primary}
            disabled={loading || isDisabledFeature}
            expanded={true}
            action={() => props.filterSearchHandler()}>
            Apply
          </Button>
        </div>
        {(isTrial || isDisabledFeature) && (
          <div className={classNames('cell', classes.AdvancedFeatureContainer)}>
            <AdvancedFeature feature={AdvancedFeatures.filterFeed} />
          </div>
        )}
      </div>
    </>
  );
};

export const ContentFilterFacebookView = (props: ContentFilterFacebookViewProps): JSX.Element => {
  const {
    formInstance,
    facebookProfiles,
    showFilters,
    setShowSaveSourceModal,
    showSaveSourceModal,
    selectedAutoCompleteValue,
    setSelectedAutoCompleteValue,
    getFacebookInputArgs,
    submitFormHandler,
    thirdPartyProfiles,
    profileSearchHistory,
    autoCompleteLoading,
    filterSearchHandler,
    apolloErrorKey,
    hasPosts,
    resetCallBackHandler
  } = props;
  const { loading, error, searchStep } = useContext(FindContentContext);

  const bodyRef = useRef<HTMLElement>(document.body);
  const { width } = useDimensions(bodyRef);

  // This returns true as long as there is any page (connected or disconnected) in the Workspace.
  const hasPages = facebookProfiles.filter(p => p.type === 'page').length > 0;
  const pagesElementValue = useWatch({ control: formInstance.control, name: 'pages' });
  const showHistoryLabel = !pagesElementValue && !!profileSearchHistory.length;

  const dropdownTemplate: SelectDropdownTemplate = useCallback(
    args => {
      const { expanded, items, setExpanded } = args;
      const filtered3rdParty = showHistoryLabel ? profileSearchHistory : thirdPartyProfiles;

      const props: autoCompleteProfileDropDownTemplateProps = {
        sourceType: SocialNetworksEnum.facebook,
        expanded,
        showHistoryLabel,
        items,
        setExpanded,
        selectedValue: selectedAutoCompleteValue,
        thirdPartyProfiles: filtered3rdParty,
        submitFormHandler: (value: string) => {
          const selectedItem = items.find(i => {
            return typeof i !== 'string' && i.value === value;
          });
          const label = selectedItem && typeof selectedItem !== 'string' ? selectedItem.label : selectedItem;

          formInstance.setValue('pages', label);

          setSelectedAutoCompleteValue(value);
          submitFormHandler(FacebookSearchType.PAGE, value);
        }
      };

      return autoCompleteProfileDropDownTemplate(props);
    },
    [
      formInstance,
      profileSearchHistory,
      selectedAutoCompleteValue,
      setSelectedAutoCompleteValue,
      showHistoryLabel,
      submitFormHandler,
      thirdPartyProfiles
    ]
  );

  const thirdPartyPagesList = useMemo(
    () =>
      generateProfileList(
        thirdPartyProfiles,
        profileSearchHistory,
        selectedAutoCompleteValue,
        pagesElementValue,
        'externalId'
      ),
    [thirdPartyProfiles, selectedAutoCompleteValue, pagesElementValue, profileSearchHistory]
  );

  const profileListItems = useMemo(() => {
    if (facebookProfiles.length === 0) {
      return [{ label: 'No Profiles', value: -1 }];
    }

    return facebookProfiles.map(p => ({ label: p.displayName, value: p.id }));
  }, [facebookProfiles]);

  const typeListItems = useMemo(() => {
    const searchPages = hasPages
      ? ['Search Facebook', { label: 'Facebook Pages', value: FacebookSearchType.PAGE }]
      : [];
    const own = ['Own Profiles', { label: 'My Posts', value: FacebookSearchTypesEnum.MY_POSTS }];

    return facebookProfiles.length > 0 ? [...searchPages, ...own] : [{ label: 'No profile connected', value: -1 }];
  }, [facebookProfiles.length, hasPages]);

  const searchTypeWrapperClasses = classNames('cell', classes.InputWrapper, {
    'small-6': width < BreakpointsValuesEnum.large
  });
  const thirdSearchWrapperClasses = classNames('cell', classes.InputWrapper, {
    'small-12': width < BreakpointsValuesEnum.large,
    auto: width >= BreakpointsValuesEnum.large,
    [classes.InputWrapperDefaultMaxWidth]: width >= BreakpointsValuesEnum.large
  });
  const socialWrapperClasses = classNames(classes.SocialWrapper);
  const labelWrapperClasses = classNames('cell auto', classes.LabelWrapper);

  const itemTemplate: SelectItemTemplate = (props: SelectViewProps, index: number, item: SelectItem) => {
    return (
      <ListItem
        key={index}
        role={'option'}
        aria-label={item.label}
        variation={'regular'}
        type={'div'}
        selected={item.value === props.selectedValue}
        action={() => props.updateValue(item.value)}
        disabled={false}>
        <div className={'grid-x'}>
          <div className={socialWrapperClasses}>
            <SocialProfile
              socialProfile={{
                id: facebookProfiles[index].id,
                provider: SocialOAuthProvider.FACEBOOK,
                network: Network.FACEBOOK,
                name: facebookProfiles[index].displayName
              }}
              disconnected={!facebookProfiles[index].connected}
              size={'small'}
            />
          </div>
          <div className={labelWrapperClasses}>{item.label}</div>
        </div>
      </ListItem>
    );
  };

  const typeValue = useWatch({ control: formInstance.control, name: 'type' });

  return (
    <>
      <div className={searchTypeWrapperClasses}>
        <Select
          label={'Search Type'}
          items={typeListItems}
          name={'type'}
          size={'medium'}
          rules={{}}
          useFormInstance={formInstance}
          disabled={loading || facebookProfiles.length < 1}
        />
      </div>
      {typeValue === FacebookSearchType.PAGE && (
        <div className={thirdSearchWrapperClasses}>
          <SelectAutoComplete
            name={'pages'}
            label={'Search Facebook Pages'}
            placeholder={'Enter keyword or page name'}
            items={thirdPartyPagesList}
            formInstance={formInstance}
            size={'medium'}
            dropdownTemplate={dropdownTemplate}
            disabled={facebookProfiles.length === 0}
            loading={autoCompleteLoading}
            callBackHandler={(value: string) => {
              submitFormHandler(FacebookSearchTypesEnum.FACEBOOK_PAGES, value);
            }}
            resetCallBackHandler={resetCallBackHandler}
          />
        </div>
      )}
      {typeValue === FacebookSearchTypesEnum.MY_POSTS && (
        <div className={thirdSearchWrapperClasses}>
          <Select
            items={profileListItems}
            name={FacebookSearchTypesEnum.MY_POSTS}
            rules={{}}
            size={'medium'}
            useFormInstance={formInstance}
            label={'Which Profile’s Timeline?'}
            chooseLabel={'Choose a Profile'}
            hideDefaultSelectOption={true}
            itemTemplate={itemTemplate}
            selectedValue={facebookProfiles.length === 0 ? -1 : undefined}
            disabled={loading || facebookProfiles.length === 0}
            action={(value, complete) => {
              complete(true);
              const profile = facebookProfiles.find(p => p.id === value);
              const search = profile?.type === 'group' ? FacebookSearchType.GROUP : FacebookSearchTypesEnum.MY_POSTS;

              if (value && typeof value === 'string') {
                submitFormHandler(search, value);
              }
            }}
          />
        </div>
      )}
      <ContentFilterSubFilterWrapper
        showFilters={
          showFilters && ((searchStep === SearchStepEnum.ONE && hasPosts) || searchStep > SearchStepEnum.ONE)
        }>
        <FacebookFilterBar formInstance={formInstance} filterSearchHandler={filterSearchHandler} width={width} />
      </ContentFilterSubFilterWrapper>
      {/* Only show if we have posts from the 1 step */}
      {((showFilters && !error) || (searchStep === SearchStepEnum.TWO && !error)) && (
        <SourceLevelAction
          formInstance={formInstance}
          setShowSaveSourceModal={setShowSaveSourceModal}
          showSaveSourceModal={showSaveSourceModal}
          getArgsHandler={getFacebookInputArgs}
        />
      )}
      {error && !loading && (
        <div className={'cell'}>
          <SearchErrorBox apolloErrorKey={apolloErrorKey} filterSearchHandler={filterSearchHandler} />
        </div>
      )}
    </>
  );
};
